import React, { useCallback, useEffect, useState } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import useForm from "../../../../hooks/useForm";
import Button from "../../../form/Button";
import DragDropfile from "../../../form/DragDropfile";
import Form from "../../../form/Form";
import { addTimeToDate, parseTimeString } from "../../../../helpers";
import {
  eventAddEdit,
  eventDetails,
  imageDelete,
} from "../../../../services/eventService";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useOrganization } from "../../../../context/OrganizationContext";
import dayjs from "dayjs";
import EventDetails from "../event-manage/EventDetails";
import Venue from "../event-manage/Venue";
import Tips from "../event-manage/Tips";
import Summary from "../event-manage/Summary";
import Artists from "../event-manage/Artists";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import LangSwitch from "../../../elements/LangSwitch";
import { countryList } from "../../../../services/countryService";
import { useDebounce } from "../../../../hooks/useDebounce";

const validation = {
  title: { required: true, message: "Title is required" },
  excerpt: [
    { required: true, message: "Excerpt is required" },
    {
      type: "range",
      errorType: "info",
      max: 800,
      message: "Maximun limit reached",
    },
  ],
  locationStatus: { required: true, message: "Event type is required" },
  start_end_date: { required: true, message: "Date is required" },
  startTime: { required: true, message: "Start time is required" },
  endTime: { required: true, message: "End time is required" },
  faqs: {
    type: "arrayValidation",
    message: "All FAQ fields must be completed",
    validationField: ["title", "excerpt"],
    required: false,
  },
};

const EventManageForm = ({
  updateEvent = () => {},
  eventRefetch = () => {},
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { eventId } = useParams();
  const { selectedOrganization } = useOrganization();
  const { selectedOrganizationDetails } = useSelector(
    (state) => state?.organization
  );
  const { lang } = useSelector((state) => state?.langProvider);
  const [selectedLang, setSelectedLang] = useState(lang || "en");

  const {
    values,
    handleChange,
    handleSubmit,
    errors,
    setFieldsValue,
    formValidate,
  } = useForm({
    validationRules: validation,
    initialState: {
      faqs: [],
      artists: [],
    },
  });

  const { data: details, error: eventError } = useQuery({
    queryKey: ["event-details-data", eventId],
    queryFn: async () => {
      const res = await eventDetails({ id: eventId, type: "front" });
      return res;
    },
    enabled: !!eventId,
  });

  useEffect(() => {
    return () => {
      queryClient.removeQueries({ queryKey: ["event-details-data", eventId] });
    };
  }, []);

  const updateValues = ({ data }) => {
    setFieldsValue({
      gallery: data?.gallery?.map((item) => ({
        ...item,
        name: item?.filename,
        type: "image",
      })),
      overView: data?.overView?.map((item) => ({
        ...item,
        id: item?._id,
        value:
          item?.type === "text"
            ? item?.content?.[selectedLang]
            : item?.type === "image" && item?.file?.url
            ? { ...item?.file, type: "image" }
            : item?.url,
      })),
      title: data?.title?.[selectedLang] || "",
      excerpt: data?.excerpt?.[selectedLang] || "",
      locationStatus: data?.locationStatus,
      start_end_date: {
        startDate: data?.startDate,
        endDate: data?.endDate,
      },
      startTime: data?.startDate
        ? dayjs(data?.startDate).format("hh:mm:ss A")
        : null,
      endTime: data?.endDate ? dayjs(data?.endDate).format("hh:mm:ss A") : null,
      faqs:
        data?.faqData?.map((item) => ({
          title: item?.title?.[selectedLang],
          excerpt: item?.excerpt?.[selectedLang],
        })) || [],
      checkInDuration: data?.others?.checkIn?.duration,
      checkInUnit: data?.others?.checkIn?.unit,
      ageRestrictionType: data?.others?.ageRestriction?.type,
      ageRestrictionValue: data?.others?.ageRestriction?.value,
      parkingType: data?.others?.parkingType,
      address: data?.address,
      artists: data?.artists || [],
      currency: {
        ...data?.currency,
        value: data?.currency?.code,
      },
    });
  };

  useEffect(() => {
    if (details?.data?._id) {
      updateValues({ data: details?.data });
    } else {
      setFieldsValue({
        currency: {
          ...selectedOrganizationDetails?.data?.setting?.currency,
          value: selectedOrganizationDetails?.data?.setting?.currency?.code,
        },
      });
    }
  }, [details?.data, selectedLang, selectedOrganizationDetails]);

  const {
    mutate: handleEventManage,
    error,
    isPending,
  } = useMutation({
    mutationFn: async (val) => {
      const overView = val?.overView
        ?.map((item, i) => {
          if (item?.type === "image" && !item?.value) {
            return undefined;
          } else {
            const overview_item = {
              ...item,
              ordering: i,
              ...(item?.type === "text"
                ? {
                    content: {
                      ...details?.data?.overView?.[i]?.content,
                      [selectedLang]: item?.value,
                    },
                  }
                : item?.type === "image"
                ? {
                    file: item?.value,
                    ...(item?.value?.url && { fileType: "old" }),
                  }
                : { url: item?.value }),
            };
            delete overview_item?.value;
            return overview_item;
          }
        })
        ?.filter(Boolean);

      const payload = {
        ...val,
        artists: val?.artists?.length === 0 ? "" : val?.artists,
        faqs:
          val?.faqs?.length > 0
            ? val?.faqs?.map((item, i) => ({
                title: {
                  ...details?.data?.faqData?.[i]?.title,
                  [selectedLang]: item?.title,
                },
                excerpt: {
                  ...details?.data?.faqData?.[i]?.excerpt,
                  [selectedLang]: item?.excerpt,
                },
              }))
            : "",
        organization: selectedOrganization || null,
        title: { ...details?.data?.title, [selectedLang]: val?.title },
        excerpt: {
          ...details?.data?.excerpt,
          [selectedLang]: val?.excerpt,
        },
        startDate: val?.start_end_date?.startDate
          ? addTimeToDate(
              val?.start_end_date?.startDate,
              parseTimeString(val?.startTime)
            )
          : null,
        endDate: val?.start_end_date?.endDate
          ? addTimeToDate(
              val?.start_end_date?.endDate,
              parseTimeString(val?.endTime)
            )
          : null,
        others: {
          checkIn: { duration: val?.checkInDuration, unit: val?.checkInUnit },
          ageRestriction: {
            type: val?.ageRestrictionType,
            value: val?.ageRestrictionValue,
          },
          parkingType: val?.parkingType,
        },
        overView,
        currency: {
          code: val?.currency?.value,
          name: val?.currency?.name,
          symbol: val?.currency?.symbol,
        },
        ...(details?.data?._id && { id: details?.data?._id }),
      };

      delete payload?.start_end_date;
      delete payload?.checkInDuration;
      delete payload?.checkInUnit;
      delete payload?.ageRestrictionType;
      delete payload?.ageRestrictionValue;
      delete payload?.startTime;
      delete payload?.endTime;
      delete payload?.parkingType;

      for (const item of overView || []) {
        if (item?.type === "image" && !item?.file) {
          toast.info(
            `Summary section ${item?.ordering}, ${item?.type} is empty`
          );
          return;
        }
      }
      return await eventAddEdit(payload);
    },
    onSuccess: (res) => {
      if (res?.status === 201) {
        toast.success(res?.message || "create successfully");
        navigate(`/${lang}/user/events/event-manage/${res?.data?.uid}`);
        return;
      }
      if (res?.status === 200) {
        updateValues({ data: res?.data });
        updateEvent(res?.data);
        toast.success(res?.message || "Update successfully");
      } else if (res?.status === 500) {
        toast.error(res?.error || res?.message || "Server Error");
      } else {
        toast.error(res?.error || res?.message);
      }
    },
    onError: (err) => {
      console.log(err);
    },
  });

  return (
    <Form handleSubmit={handleSubmit} onSubmit={handleEventManage}>
      <div className="p-4 pb-0">
        <LangSwitch
          langs={["en", "es"]}
          selectedLang={selectedLang}
          setSelectedLang={setSelectedLang}
        />
      </div>
      <div className="bg-white py-4 px-4 rounded-md">
        <div className="space-y-6">
          <DragDropfile
            formats={".jpg, .png, .jpeg"}
            isMultiple
            value={values?.gallery}
            handleFile={(file) => {
              handleChange({ target: { name: "gallery", value: file } });
            }}
            onRemove={async (file) => {
              const key = typeof file === "string" ? null : file?.key;

              // Use ID from fetched event details
              const eventId = details?.data?._id;

              if (!key || !eventId) return;

              try {
                await imageDelete({ key, id: eventId });
              } catch (err) {
                console.error("Delete failed:", err);
              }
            }}
          />
          <EventDetails
            errors={errors}
            formValidate={formValidate}
            handleChange={handleChange}
            values={values}
            data={details?.data}
          />
          <Venue
            errors={errors}
            formValidate={formValidate}
            handleChange={handleChange}
            values={values}
            setFieldsValue={setFieldsValue}
            data={details?.data}
          />
          <Summary
            errors={errors}
            formValidate={formValidate}
            handleChange={handleChange}
            values={values}
            setFieldsValue={setFieldsValue}
            data={details?.data}
          />
          <Tips
            errors={errors}
            formValidate={formValidate}
            handleChange={handleChange}
            values={values}
            setFieldsValue={setFieldsValue}
            data={details?.data}
          />
          <Artists
            errors={errors}
            formValidate={formValidate}
            handleChange={handleChange}
            values={values}
            setFieldsValue={setFieldsValue}
            data={details?.data}
          />
        </div>
      </div>
      <div className="flex gap-3 justify-end bg-magnolia py-4 sticky bottom-0 right-0 z-[2]">
        <Button
          buttonType="submit"
          {...(isPending && {
            loading: true,
            buttonIconPosition: "left",
            isDisable: true,
          })}
          buttonLabel={details?.data?._id ? "Update Event" : "save Event"}
        />
        {details?.data?.uid && (
          <Button
            variant="secondary"
            buttonLabel={"Create Ticket"}
            buttonFunction={() => {
              navigate(
                `/${lang}/user/events/manage-ticket/${details?.data?.uid}`
              );
            }}
          />
        )}
      </div>
    </Form>
  );
};

export default EventManageForm;
